import { useCallback } from 'react';
import { setCookie } from 'cookies-next';

interface CookieOptions {
  maxAge?: number; // in seconds
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
}

const DEFAULT_COOKIE_OPTIONS: CookieOptions = {
  maxAge: 30 * 24 * 60 * 60, // 30 days in seconds
  path: '/',
  sameSite: 'lax',
  secure: process.env.NODE_ENV === 'production',
};

export const useCookies = () => {
  // Set a cookie with optional custom options
  const setCookieValue = useCallback(
    (name: string, value: string, options: CookieOptions = {}) => {
      try {
        const cookieOptions = { ...DEFAULT_COOKIE_OPTIONS, ...options };
        setCookie(name, value, cookieOptions);
      } catch (error) {
        // Silently handle cookie setting errors
      }
    },
    [],
  );

  // Set a JSON object as a cookie value
  const setCookieObject = useCallback(
    (name: string, value: object, options: CookieOptions = {}) => {
      try {
        const jsonValue = JSON.stringify(value);
        setCookieValue(name, jsonValue, options);
      } catch (error) {
        // Silently handle JSON serialization errors
      }
    },
    [setCookieValue],
  );

  // Set a cookie with the default options (convenience method)
  const setDefaultCookie = useCallback(
    (name: string, value: string) => {
      setCookieValue(name, value);
    },
    [setCookieValue],
  );

  return {
    setCookieValue,
    setCookieObject,
    setDefaultCookie,
  };
};
